import React, { useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { useTranslation } from '../hooks/useTranslation';
import { supabase } from '../lib/supabase';
import { useAuthStore } from '../store/useAuthStore';
import { AlertCircle, CheckCircle, Loader2, ClipboardList, Car, Wrench } from 'lucide-react';

export function HomePage() {
  const { t } = useTranslation();
  const { user } = useAuthStore();
  const navigate = useNavigate();
  const [email, setEmail] = useState('');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [success, setSuccess] = useState(false);

  const handleSubscribe = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!email) return;

    setLoading(true);
    setError(null);
    setSuccess(false);

    try {
      const { count, error: countError } = await supabase
        .from('newsletter_subscriptions')
        .select('*', { count: 'exact', head: true })
        .eq('email', email)
        .eq('status', 'active');

      if (countError) throw countError;

      if (count && count > 0) {
        setError('This email is already subscribed to our newsletter');
        return;
      }

      const { error: subscribeError } = await supabase
        .from('newsletter_subscriptions')
        .insert([{ 
          email,
          status: 'active',
          subscribed_at: new Date().toISOString()
        }]);

      if (subscribeError) throw subscribeError;

      setSuccess(true);
      setEmail('');
    } catch (err) {
      console.error('Error subscribing to newsletter:', err);
      setError('Failed to subscribe. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  const handleGetStarted = () => {
    if (user) {
      navigate('/dashboard');
    } else {
      navigate('/login');
    }
  };

  return (
    <div className="flex flex-col min-h-screen">
      {/* Hero Section */}
      <div 
        className="relative bg-cover bg-center min-h-[60vh] md:h-[600px] flex items-center"
        style={{
          backgroundImage: 'linear-gradient(rgba(0, 0, 0, 0.5), rgba(0, 0, 0, 0.5)), url("https://images.unsplash.com/photo-1492144534655-ae79c964c9d7?auto=format&fit=crop&q=80")'
        }}
      >
        <div className="absolute inset-0 flex items-center justify-center p-4">
          <div className="text-center max-w-xl mx-auto">
            <h1 className="text-3xl md:text-6xl font-bold mb-4 text-white">
              {t.home.hero.title}
            </h1>
            <p className="text-lg md:text-2xl mb-8 text-white">
              {t.home.hero.subtitle}
            </p>
            <Link
              to="/services"
              className="inline-block bg-blue-600 text-white px-6 py-3 rounded-lg text-lg font-semibold hover:bg-blue-700 transition-colors touch-manipulation"
            >
              {t.home.hero.cta}
            </Link>
          </div>
        </div>
      </div>

      {/* Steps Section */}
      <section className="py-12 md:py-16 bg-gray-50 dark:bg-gray-800">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-12">
            <h2 className="text-2xl md:text-3xl font-bold text-gray-900 dark:text-white mb-4">
              {t.home.steps.title}
            </h2>
          </div>

          <div className="grid md:grid-cols-3 gap-6 md:gap-8">
            {/* Step 1 */}
            <div className="bg-white dark:bg-gray-700 rounded-lg p-6 md:p-8 text-center shadow-lg transform hover:scale-105 transition-transform">
              <div className="flex justify-center mb-4">
                <div className="w-16 h-16 bg-blue-100 dark:bg-blue-900 rounded-full flex items-center justify-center">
                  <ClipboardList className="w-8 h-8 text-blue-600 dark:text-blue-400" />
                </div>
              </div>
              <h3 className="text-xl font-bold text-gray-900 dark:text-white mb-2">
                {t.home.steps.step1.title}
              </h3>
              <h4 className="text-lg font-semibold text-blue-600 dark:text-blue-400 mb-2">
                {t.home.steps.step1.heading}
              </h4>
              <p className="text-gray-600 dark:text-gray-300 text-sm md:text-base">
                {t.home.steps.step1.description}
              </p>
            </div>

            {/* Step 2 */}
            <div className="bg-white dark:bg-gray-700 rounded-lg p-6 md:p-8 text-center shadow-lg transform hover:scale-105 transition-transform">
              <div className="flex justify-center mb-4">
                <div className="w-16 h-16 bg-blue-100 dark:bg-blue-900 rounded-full flex items-center justify-center">
                  <Car className="w-8 h-8 text-blue-600 dark:text-blue-400" />
                </div>
              </div>
              <h3 className="text-xl font-bold text-gray-900 dark:text-white mb-2">
                {t.home.steps.step2.title}
              </h3>
              <h4 className="text-lg font-semibold text-blue-600 dark:text-blue-400 mb-2">
                {t.home.steps.step2.heading}
              </h4>
              <p className="text-gray-600 dark:text-gray-300 text-sm md:text-base">
                {t.home.steps.step2.description}
              </p>
            </div>

            {/* Step 3 */}
            <div className="bg-white dark:bg-gray-700 rounded-lg p-6 md:p-8 text-center shadow-lg transform hover:scale-105 transition-transform">
              <div className="flex justify-center mb-4">
                <div className="w-16 h-16 bg-blue-100 dark:bg-blue-900 rounded-full flex items-center justify-center">
                  <Wrench className="w-8 h-8 text-blue-600 dark:text-blue-400" />
                </div>
              </div>
              <h3 className="text-xl font-bold text-gray-900 dark:text-white mb-2">
                {t.home.steps.step3.title}
              </h3>
              <h4 className="text-lg font-semibold text-blue-600 dark:text-blue-400 mb-2">
                {t.home.steps.step3.heading}
              </h4>
              <p className="text-gray-600 dark:text-gray-300 text-sm md:text-base">
                {t.home.steps.step3.description}
              </p>
            </div>
          </div>

          {/* Call to Action Button */}
          <div className="text-center mt-12">
            <button
              onClick={handleGetStarted}
              className="inline-block bg-blue-600 text-white px-6 py-3 rounded-lg text-lg font-semibold hover:bg-blue-700 transition-colors touch-manipulation"
            >
              {user ? t.common.goToDashboard : t.common.getStarted}
            </button>
          </div>
        </div>
      </section>

      {/* Newsletter Section */}
      <section className="py-12 md:py-16 bg-white dark:bg-gray-900">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center max-w-md mx-auto">
            <h2 className="text-2xl md:text-3xl font-bold mb-4 text-gray-900 dark:text-white">
              {t.home.newsletter.title}
            </h2>
            <p className="text-gray-600 dark:text-gray-300 mb-8 text-sm md:text-base">
              {t.home.newsletter.subtitle}
            </p>
            <form onSubmit={handleSubscribe} className="space-y-4">
              <div className="flex flex-col md:flex-row gap-2">
                <input
                  type="email"
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  placeholder={t.home.newsletter.placeholder}
                  className="flex-1 px-4 py-2 rounded-lg border border-gray-300 dark:border-gray-600 dark:bg-gray-800 dark:text-white text-base"
                  required
                />
                <button
                  type="submit"
                  disabled={loading || !email}
                  className="px-6 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors disabled:opacity-50 disabled:cursor-not-allowed flex items-center justify-center touch-manipulation"
                >
                  {loading ? (
                    <>
                      <Loader2 className="w-4 h-4 animate-spin mr-2" />
                      <span>Subscribing...</span>
                    </>
                  ) : (
                    t.home.newsletter.button
                  )}
                </button>
              </div>

              {error && (
                <div className="flex items-center text-red-600 dark:text-red-400 text-sm">
                  <AlertCircle className="w-4 h-4 mr-2 flex-shrink-0" />
                  <span>{error}</span>
                </div>
              )}

              {success && (
                <div className="flex items-center text-green-600 dark:text-green-400 text-sm">
                  <CheckCircle className="w-4 h-4 mr-2 flex-shrink-0" />
                  <span>{t.home.newsletter.success}</span>
                </div>
              )}
            </form>
          </div>
        </div>
      </section>
    </div>
  );
}